/**
 * HeaderCreateMenu View Component
 *
 * Displays the create menu popover with expandable submenus for recent players.
 * @module components/HeaderCreateMenu
 */

import {
  ChevronDown,
  ChevronUp,
  FileText,
  ListPlus,
  ScrollText,
} from "lucide-react-native";
import React, { memo } from "react";
import { Platform } from "react-native";

import { Icon } from "@/components/atoms/Icon";
import { Pressable } from "@/components/atoms/Pressable";
import {
  Popover,
  PopoverBackdrop,
  PopoverBody,
  PopoverContent,
} from "@/components/atoms/Popover";
import { Text } from "@/components/atoms/Text";
import { Stack } from "@/components/atoms/Stack";
import { ifWeb } from "@/utils/platform";

import PlayerListSubmenu from "./PlayerListSubmenu";

/**
 * Recent player item for display in menus
 */
interface RecentPlayerItem {
  id: string;
  name: string;
}

/**
 * Expanded menu action type
 */
type ExpandedAction = "report" | "note" | "watchlist" | null;

/**
 * Props for HeaderCreateMenuView component
 */
interface Props {
  isOpen: boolean;
  onClose: () => void;
  onOpen: () => void;
  isDark: boolean;
  expandedAction: ExpandedAction;
  recentPlayers: readonly RecentPlayerItem[];
  onWriteReport: () => void;
  onAddNote: () => void;
  onAddToWatchlist: () => void;
  onSelectPlayerForReport: (playerId: string) => void;
  onSelectPlayerForNote: (playerId: string) => void;
  onSelectPlayerForWatchlist: (playerId: string) => void;
  renderTrigger: (triggerProps: Record<string, unknown>) => React.ReactElement;
}

/**
 * Get theme-based class names
 * @param isDark - Whether dark theme is active
 */
const getThemeClasses = (isDark: boolean) => ({
  popoverContent: `mr-6 ${ifWeb("min-w-72", "min-w-80")} rounded-lg border-0 px-0 py-2 ${isDark ? "bg-surface-action" : "bg-white"}`,
  menuItem: `flex-row items-center gap-3 px-4 py-3 ${isDark ? "active:bg-outline-emphasis" : "active:bg-surface-subtle"}`,
  menuItemExpanded: `flex-row items-center gap-3 px-4 py-3 ${isDark ? "" : "bg-surface-subtle"}`,
  menuIcon: "text-content-muted",
  menuText: `flex-1 ${isDark ? "text-white" : "text-content-primary"}`,
  chevronIcon: "text-content-subtle",
});

/**
 * Pure view component for header create menu popover
 * @param root0 - Component props
 * @param root0.isOpen - Whether popover is open
 * @param root0.onClose - Handler for popover close
 * @param root0.onOpen - Handler for popover open
 * @param root0.isDark - Whether dark theme is active
 * @param root0.expandedAction - Currently expanded action
 * @param root0.recentPlayers - List of recent players
 * @param root0.onWriteReport - Handler for write report action
 * @param root0.onAddNote - Handler for add note action
 * @param root0.onAddToWatchlist - Handler for add to watchlist action
 * @param root0.onSelectPlayerForReport - Handler for selecting player for report
 * @param root0.onSelectPlayerForNote - Handler for selecting player for note
 * @param root0.onSelectPlayerForWatchlist - Handler for selecting player for watchlist
 * @param root0.renderTrigger - Render function for the popover trigger button
 */
const HeaderCreateMenuView = ({
  isOpen,
  onClose,
  onOpen,
  isDark,
  expandedAction,
  recentPlayers,
  onWriteReport,
  onAddNote,
  onAddToWatchlist,
  onSelectPlayerForReport,
  onSelectPlayerForNote,
  onSelectPlayerForWatchlist,
  renderTrigger,
}: Props) => (
  <Popover
    isOpen={isOpen}
    onClose={onClose}
    onOpen={onOpen}
    placement="bottom"
    offset={8}
    crossOffset={Platform.OS === "android" ? -88 : 0}
    size="md"
    trigger={renderTrigger}
    useRNModal
  >
    <PopoverBackdrop />
    <PopoverContent
      key={`create-menu-${isDark ? "dark" : "light"}`}
      className={getThemeClasses(isDark).popoverContent}
    >
      <PopoverBody>
        <Stack>
          {/* Write Report */}
          <Pressable
            onPress={onWriteReport}
            accessible
            accessibilityLabel="Write Report"
            accessibilityRole="button"
            className={
              expandedAction === "report"
                ? getThemeClasses(isDark).menuItemExpanded
                : getThemeClasses(isDark).menuItem
            }
          >
            <Icon
              as={ScrollText}
              size="md"
              className={getThemeClasses(isDark).menuIcon}
            />
            <Text
              variant="title-sm-medium"
              className={getThemeClasses(isDark).menuText}
            >
              Write Report
            </Text>
            <Icon
              as={expandedAction === "report" ? ChevronUp : ChevronDown}
              size="sm"
              className={getThemeClasses(isDark).chevronIcon}
            />
          </Pressable>
          {expandedAction === "report" && (
            <PlayerListSubmenu
              players={recentPlayers}
              onSelectPlayer={onSelectPlayerForReport}
              isDark={isDark}
            />
          )}

          {/* Add Note */}
          <Pressable
            onPress={onAddNote}
            accessible
            accessibilityLabel="Add Note"
            accessibilityRole="button"
            className={
              expandedAction === "note"
                ? getThemeClasses(isDark).menuItemExpanded
                : getThemeClasses(isDark).menuItem
            }
          >
            <Icon
              as={FileText}
              size="md"
              className={getThemeClasses(isDark).menuIcon}
            />
            <Text
              variant="title-sm-medium"
              className={getThemeClasses(isDark).menuText}
            >
              Add Note
            </Text>
            <Icon
              as={expandedAction === "note" ? ChevronUp : ChevronDown}
              size="sm"
              className={getThemeClasses(isDark).chevronIcon}
            />
          </Pressable>
          {expandedAction === "note" && (
            <PlayerListSubmenu
              players={recentPlayers}
              onSelectPlayer={onSelectPlayerForNote}
              isDark={isDark}
            />
          )}

          {/* Add Player to Watchlist */}
          <Pressable
            onPress={onAddToWatchlist}
            accessible
            accessibilityLabel="Add Player to Watchlist"
            accessibilityRole="button"
            className={
              expandedAction === "watchlist"
                ? getThemeClasses(isDark).menuItemExpanded
                : getThemeClasses(isDark).menuItem
            }
          >
            <Icon
              as={ListPlus}
              size="md"
              className={getThemeClasses(isDark).menuIcon}
            />
            <Text
              variant="title-sm-medium"
              className={getThemeClasses(isDark).menuText}
            >
              Add Player to Watchlist
            </Text>
            <Icon
              as={expandedAction === "watchlist" ? ChevronUp : ChevronDown}
              size="sm"
              className={getThemeClasses(isDark).chevronIcon}
            />
          </Pressable>
          {expandedAction === "watchlist" && (
            <PlayerListSubmenu
              players={recentPlayers}
              onSelectPlayer={onSelectPlayerForWatchlist}
              isDark={isDark}
            />
          )}
        </Stack>
      </PopoverBody>
    </PopoverContent>
  </Popover>
);

HeaderCreateMenuView.displayName = "HeaderCreateMenuView";

export default memo(HeaderCreateMenuView);
